import React, { useEffect, useState } from 'react'
import injectStyles from 'react-jss'
import moment from 'moment'
import { Col, Grid, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { AmbassadorSection } from '../../../../../../Components/City/Page/Ambassador/AmbassadorSection'
import GenericFlatButton from '../../../../../../Components/GenericFlatButton'
import { styles } from './styles'
import {
  hideReduxModal,
  showReduxModal,
} from '../../../../../../Reducers/UI/actions'
import PreviewPdfModal from './modals/PreviewPdfModal'
import CancelModal from './modals/CancelModal'
import ChangeContractDataModal from './modals/ChangeContractDataModal'
import {
  CONTRACT_TYPES_ENUM,
  GERMANY_CONTRACT_ADDENDUM_TYPES,
} from '../../../../../../shared/constants'
import {
  cancelContractAddendum,
  cancelUserContract,
  createUserContract,
  createUserContractAddendum,
  createUserContractPDF,
  updateContractData,
  uploadUserContractAddendum,
  updateUserContractEndDate,
} from '../../gql'
import LoadingIndicator from '../../../../../../Components/LoadingIndicator/LoadingIndicator'
import { ADDENDUM_NAMES_MAP, statusOptions, typeSuffixMap } from './const'
import ContractItem from './components/ContractItem'
import Tabs from './components/Tabs'
import ArchivedContractItem from './components/ArchivedContractItem'
import CreateAddendumModal from './modals/CreateAddendumModal'
import ManualEndDateChangeModal from './modals/ManualEndDateChangeModal'
import { cutAddsNameToFixedWordsNumber } from './utils'

const VIEW_TYPE = {
  ACTIVE: 'ACTIVE',
  HISTORY: 'HISTORY',
}

const GermanyContractDataSection = ({
  contractData,
  ambassador,
  refetchAmbassadorData,
  isDataLoading,
  classes,
}) => {
  const [viewType, setViewType] = useState(VIEW_TYPE.ACTIVE)
  const [list, setList] = useState(null)

  useEffect(() => {
    if (!contractData) return
    if (viewType === VIEW_TYPE.ACTIVE) {
      setList(contractData.active)
    } else {
      setList(contractData.archived)
    }
  }, [contractData, viewType])

  const dispatch = useDispatch()

  const activeContract =
    contractData && contractData.active[0]
      ? contractData.active[0].contract
      : {}
  const userContractData = ambassador.germanyContractData

  const onCancelContract = async () => {
    try {
      await cancelUserContract({
        userId: ambassador.id,
        contractName: userContractData.contractType,
      })
      await cancelUserContract({
        userId: ambassador.id,
        contractName: 'GERMANY_GPS',
      })
      dispatch(hideReduxModal())
      refetchAmbassadorData()
    } catch (error) {
      dispatch(hideReduxModal())
    }
  }

  const hasNoActiveContracts =
    !contractData || !contractData.active || contractData.active.length === 0
  const hasArchivedContracts =
    contractData && contractData.archived && contractData.archived.length > 0
  const canShowManualEndDateChange =
    hasNoActiveContracts && hasArchivedContracts

  const onManualEndDateChange = async endDate => {
    try {
      const result = await updateUserContractEndDate({
        userId: ambassador.id,
        endDate,
      })
      const {
        data: {
          updateUserContractEndDate: { success },
        },
      } = result
      if (success) {
        dispatch(hideReduxModal())
        refetchAmbassadorData()
      } else {
        dispatch(hideReduxModal())
      }
    } catch (error) {
      dispatch(hideReduxModal())
    }
  }

  const generateHeaderButton = () => {
    if (isDataLoading) return <LoadingIndicator />
    if (!activeContract.url) {
      return (
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {canShowManualEndDateChange && (
            <GenericFlatButton
              type={'text'}
              title={'Manual end date change'}
              onClick={() =>
                dispatch(
                  showReduxModal({
                    children: (
                      <ManualEndDateChangeModal
                        onSubmit={onManualEndDateChange}
                        onCancel={() => dispatch(hideReduxModal())}
                      />
                    ),
                    hideConfirm: true,
                    hideCancel: true,
                    hideButtons: true,
                    hideTitle: true,
                  }),
                )
              }
              customStyles={classes.button}
            />
          )}
          <GenericFlatButton
            type={'filled'}
            title={'Generate contract'}
            onClick={onGenerateContract}
            disabled={ambassador.status !== 'active'}
          />
        </div>
      )
    }
    if (activeContract.url && !activeContract.signedAt) {
      return (
        <GenericFlatButton
          type={'outline'}
          title={'Cancel'}
          onClick={() =>
            dispatch(
              showReduxModal({
                children: (
                  <CancelModal
                    title={'Are you sure you want to cancel the contract?'}
                    onSubmit={onCancelContract}
                    onCancel={() => dispatch(hideReduxModal())}
                  />
                ),
                hideConfirm: true,
                hideCancel: true,
                hideButtons: true,
                hideTitle: true,
              }),
            )
          }
        />
      )
    }
    if (
      activeContract &&
      activeContract.signedAt &&
      viewType !== VIEW_TYPE.HISTORY
    ) {
      return (
        <GenericFlatButton
          type={'text'}
          title={'+ Create addendum'}
          onClick={() =>
            onAddAddendum({
              contract: activeContract,
              isDatesInRangeContract: false,
            })
          }
          customStyles={classes.button}
        />
      )
    }
  }

  const onChangeContractData = async ({
    contractType,
    startDate,
    hoursPerWeek,
    endDate,
  }) => {
    try {
      await updateContractData({
        userId: ambassador.id,
        startDate,
        contractType,
        hoursPerWeek,
      })

      const contract = await createUserContractPDF({
        userId: ambassador.id,
        startAt: startDate,
        endAt: endDate,
        hoursPerWeek,
        contractName: CONTRACT_TYPES_ENUM[contractType],
      })
      const url = contract.data.createUserContractPDF.url
      if (url) {
        return dispatch(
          showReduxModal({
            children: (
              <PreviewPdfModal
                title={`${ambassador.firstName} ${
                  ambassador.lastName
                } contract`}
                contractName={generateContractPreviewName(
                  CONTRACT_TYPES_ENUM[contractType],
                )}
                url={url}
                onSubmit={async () => {
                  await createUserContract({
                    userId: ambassador.id,
                    startAt: startDate,
                    endAt: endDate,
                    hoursPerWeek,
                    contractName: CONTRACT_TYPES_ENUM[contractType],
                    justPreview: false,
                  })
                  await createUserContract({
                    userId: ambassador.id,
                    startAt: startDate,
                    endAt: endDate,
                    hoursPerWeek,
                    contractName: 'GERMANY_GPS',
                    justPreview: false,
                  })
                  dispatch(hideReduxModal())
                  refetchAmbassadorData()
                }}
                onCancel={() => {
                  dispatch(hideReduxModal())
                  refetchAmbassadorData()
                }}
              />
            ),
            hideConfirm: true,
            hideCancel: true,
            hideButtons: true,
            hideTitle: true,
            aspect: 'pdfViewer',
          }),
        )
      }
    } catch (error) {
      /* eslint-disable */
      console.log({ error })
    }
  }

  const onGenerateContract = () => {
    if (!ambassador.germanyContractData) {
      return dispatch(
        showReduxModal({
          title: 'Error',
          children:
            'Information needs to be uploaded to be able to generate a new contract.',
          hideCancel: true,
          confirmLabel: `OK`,
        }),
      )
    }
    dispatch(
      showReduxModal({
        children: (
          <ChangeContractDataModal
            title={`New contract for:`}
            data={ambassador.germanyContractData}
            ambassador={ambassador}
            subtitle={`${ambassador.firstName} ${ambassador.lastName}`}
            options={statusOptions}
            currentType={3}
            onSubmit={onChangeContractData}
            onCancel={() => dispatch(hideReduxModal())}
          />
        ),
        hideConfirm: true,
        hideCancel: true,
        hideButtons: true,
        hideTitle: true,
      }),
    )
  }

  const onContractNameClick = (contract, title) => {
    return dispatch(
      showReduxModal({
        children: (
          <PreviewPdfModal
            title={
              title || `${ambassador.firstName} ${ambassador.lastName} contract`
            }
            contractName={generateContractName(contract)}
            url={contract.url}
            showButtons={false}
            onCancel={() => dispatch(hideReduxModal())}
          />
        ),
        hideConfirm: true,
        hideCancel: true,
        hideButtons: true,
        hideTitle: true,
        aspect: 'pdfViewer',
      }),
    )
  }

  const generateContractName = contract => {
    return `${ambassador.firstName}${ambassador.lastName} ${
      typeSuffixMap[contract.templateDocument.name]
    }
    ${
      contract.createdAt
        ? moment(Number(contract.createdAt)).format('D/MM')
        : moment(
            new Date(Number(contract.signedAt)),
            'DD-MM-YYYY HH:mm:ss',
          ).format('D/MM')
    } 
    ${
      contract.metadata && contract.metadata.serialNumber
        ? contract.metadata.serialNumber
        : ''
    }`
  }

  const generateContractPreviewName = type => {
    return `${ambassador.firstName}${ambassador.lastName} ${
      typeSuffixMap[type]
    }${moment().format('D/MM')}`
  }

  const tabs = [
    {
      label: 'Active',
      isActive: viewType === VIEW_TYPE.ACTIVE,
      onClick: () => setViewType(VIEW_TYPE.ACTIVE),
    },
    {
      label: 'Contract history',
      isActive: viewType === VIEW_TYPE.HISTORY,
      onClick: () => setViewType(VIEW_TYPE.HISTORY),
    },
  ]

  const generateAddendumName = (add, contract) => {
    return cutAddsNameToFixedWordsNumber(
      ADDENDUM_NAMES_MAP[add.templateDocument.name],
      4,
      add.templateDocument.name,
      contract,
      add,
    )
  }

  const onCancelAddendum = async add => {
    return dispatch(
      showReduxModal({
        children: (
          <CancelModal
            title={'Are you sure you want to cancel the addendum?'}
            onSubmit={async () => {
              await cancelContractAddendum(add.id)
              dispatch(hideReduxModal())
              refetchAmbassadorData()
            }}
            onCancel={() => dispatch(hideReduxModal())}
          />
        ),
        hideConfirm: true,
        hideCancel: true,
        hideButtons: true,
        hideTitle: true,
      }),
    )
  }

  const onAddendumCreate = async input => {
    if (
      [
        GERMANY_CONTRACT_ADDENDUM_TYPES.CHANGE_AMOUNT_HOURS,
        GERMANY_CONTRACT_ADDENDUM_TYPES.CONTRACT_EXTENSION,
      ].includes(input.name)
    ) {
      return await createUserContractAddendum(input)
    } else {
      return await uploadUserContractAddendum(input)
    }
  }

  const onAddAddendum = ({ contract, isDatesInRangeContract }) => {
    return dispatch(
      showReduxModal({
        children: (
          <CreateAddendumModal
            contractName={generateContractName(contract)}
            onCancel={() => dispatch(hideReduxModal())}
            contract={contract}
            onCreate={onAddendumCreate}
            refetchAmbassadorData={refetchAmbassadorData}
            isDatesInRangeContract={isDatesInRangeContract}
          />
        ),
        hideConfirm: true,
        hideCancel: true,
        hideButtons: true,
        hideTitle: true,
      }),
    )
  }
  const canShowTabs =
    contractData &&
    (contractData.active.length > 0 || contractData.archived.length > 0)
  return (
    <AmbassadorSection>
      <Grid fluid>
        <Row>
          <Col xs={12}>
            <div className={classes.headerContainer}>
              <div className={classes.title}>Contract</div>
              {generateHeaderButton()}
            </div>
          </Col>
        </Row>
        {!isDataLoading && canShowTabs && (
          <Row>
            <Col xs={12}>
              <Tabs tabs={tabs} />
            </Col>
          </Row>
        )}
        {!isDataLoading &&
          list &&
          list.map((contract, index) => {
            if (viewType === VIEW_TYPE.HISTORY) {
              return (
                <ArchivedContractItem
                  onContractNameClick={onContractNameClick}
                  ambassador={ambassador}
                  contract={contract}
                  index={index}
                  generateContractName={generateContractName}
                  onCancelAddendum={onCancelAddendum}
                  generateAddendumName={generateAddendumName}
                  onAddAddendum={onAddAddendum}
                />
              )
            }
            return (
              <ContractItem
                onContractNameClick={onContractNameClick}
                ambassador={ambassador}
                contract={contract}
                generateContractName={generateContractName}
                onCancelAddendum={onCancelAddendum}
                generateAddendumName={generateAddendumName}
              />
            )
          })}
      </Grid>
    </AmbassadorSection>
  )
}

export default injectStyles(styles)(GermanyContractDataSection)
